import { useState, useEffect } from "react";
import axios from "axios";
import InquiriesTable from "../components/inquiryTable";
import Pagination from "../components/pagination";
import Layout from "../containers/layout";
import Cookies from "js-cookie";
import * as XLSX from "xlsx";
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Spinner,
  Box,
  Grid,
  Toast,
} from "@shopify/polaris";

const Inquiries = () => {
  const [products, setProducts] = useState([]);
  const [currentProducts, setCurrentProducts] = useState([]);
  const [indexing, setIndexing] = useState(1);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState({ from: "", till: "" });
  const [notification, setNotification] = useState(null);

  const productPerPage = 20;
  const token = Cookies.get("token");

  const formatDate = (date) => {
    return date.split("-").reverse().join("-");
  };

  const getCurrentDate = () => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    const padZero = (num) => (num < 10 ? `0${num}` : num);
    let formattedDate = `${padZero(day)}-${padZero(month)}-${year}`;
    return formatDate(formattedDate);
  };

  const getDate = (date) => {
    const formattedDate = new Date(date);
    return formattedDate.toLocaleDateString("en-GB");
  };

  const showNotification = (tone, title) => {
    setNotification({ tone, title });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    if (date.till && date.from > date.till) {
      showNotification("critical", "From date cannot be after Till date");
    }
  }, [date]);

  useEffect(() => {
    if (formatDate(getCurrentDate()) === date.from) {
      setDate({ ...date, till: date.from });
    }
  }, [date.from]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = {
          method: "GET",
          url: "https://api.iplanet.one/casaformdata/allleads",
          headers: { Authorization: token },
        };
        const response = await axios.request(config);
        const leads = Array.isArray(response?.data?.leads)
          ? response.data.leads
          : [];
        setProducts(leads);
      } catch (error) {
        console.error("Error fetching leads:", error);
        setProducts([]);
        showNotification("critical", "Failed to load inquiries");
      }
    };

    fetchData();
  }, []);

  const formatDateDMY = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const FilterHandler = async () => {
    setLoading(true);
    try {
      const payload = {
        from: formatDateDMY(date.from),
        till: formatDateDMY(date.till),
      };
      const config = {
        method: "post",
        url: "https://api.iplanet.one/casaformdata/filterleads",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      };
      const response = await axios.request(config);
      const leads = Array.isArray(response?.data?.leads)
        ? response.data.leads
        : [];
      setProducts(leads);
      setCurrentProducts([]);
      setIndexing(1);

      if (leads.length === 0) {
        showNotification("info", "No Data Found");
      } else {
        showNotification("success", "Inquiries filtered successfully");
      }
    } catch (error) {
      console.error("Filter error:", error);
      showNotification("critical", "Failed to filter inquiries");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    try {
      const cleanedLeads = products.map(
        ({ __v, updatedAt, createdAt, customer, ...rest }) => ({
          ...rest,
          createdAt: getDate(createdAt),
          ...customer,
        }),
      );
      const worksheet = XLSX.utils.json_to_sheet(cleanedLeads);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
      XLSX.writeFile(workbook, "leads_data.xlsx");
      showNotification("success", "Data exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      showNotification("critical", "Failed to export data");
    }
  };

  return (
    <Layout>
      <Page fullWidth>
        <BlockStack gap="500">
          {notification && (
            <Toast
              content={notification.title}
              tone={notification.tone}
              onDismiss={() => setNotification(null)}
            />
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingLg" as="h1">
                  Inquiries
                </Text>
                <Button variant="primary" onClick={handleDownload}>
                  Export to Excel
                </Button>
              </InlineStack>

              <Card background="bg-surface-secondary" rounded="300">
                <Box padding="400">
                  <BlockStack gap="400">
                    <Grid
                      columns={{ xs: 1, sm: 6, md: 12, lg: 12 }}
                      gap={{ xs: "400", md: "500" }}
                    >
                      <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                        <TextField
                          label="From Date"
                          type="date"
                          value={date.from}
                          onChange={(value) =>
                            setDate({ ...date, from: value })
                          }
                          max={getCurrentDate()}
                          autoComplete="off"
                        />
                      </Grid.Cell>
                      <Grid.Cell columnSpan={{ xs: 1, md: 5, lg: 5 }}>
                        <TextField
                          label="Till Date"
                          type="date"
                          value={date.till}
                          onChange={(value) =>
                            setDate({ ...date, till: value })
                          }
                          min={date.from || undefined}
                          max={getCurrentDate()}
                          autoComplete="off"
                        />
                      </Grid.Cell>
                      <Grid.Cell columnSpan={{ xs: 1, md: 2, lg: 2 }}>
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "flex-center",
                            paddingTop: "var(--p-space-600)",
                          }}
                        >
                          <Button
                            variant="primary"
                            onClick={FilterHandler}
                            loading={loading}
                            disabled={
                              loading ||
                              !date.from ||
                              !date.till ||
                              date.from > date.till
                            }
                            fullWidth
                          >
                            Filter
                          </Button>
                        </div>
                      </Grid.Cell>
                    </Grid>
                  </BlockStack>
                </Box>
              </Card>
            </BlockStack>
          </Card>
          <Card>
            {loading ? (
              <div style={{ padding: "120px 0", textAlign: "center" }}>
                <Spinner accessibilityLabel="Loading" size="large" />
                <div style={{ marginTop: "1rem" }}>
                  <Text variant="bodyMd" tone="subdued">
                    Loading inquiries...
                  </Text>
                </div>
              </div>
            ) : (
              <BlockStack gap="400">
                <InquiriesTable
                  products={currentProducts}
                  indexing={indexing}
                />
                <div
                  style={{
                    padding: "40px 0",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    products={products}
                    productPerPage={productPerPage}
                    setCurrentProducts={setCurrentProducts}
                    setIndexing={setIndexing}
                    indexing={indexing}
                    currentProducts={currentProducts}
                  />
                </div>
              </BlockStack>
            )}
          </Card>
        </BlockStack>
      </Page>
    </Layout>
  );
};

export default Inquiries;
